import type { SchedulerJobResponse } from '@inflip/contracts/admin';
import {
  BUSINESS,
  SCHEDULER_JOB_STATUS_LABELS,
  type SchedulerJobKey,
  type SchedulerJobStatus,
} from '@inflip/shared';
import { useCallback, useEffect, useState } from 'react';

import {
  BodyRow,
  Button,
  ConfirmDialog,
  ErrorBanner,
  LoadingRow,
  PageHeader,
  TableShell,
  Td,
  Th,
  TheadRow,
} from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { formatDateTime } from '../lib/time-zone';
import { showToast } from '../lib/toast';

/** 실행 요청 뒤 결과가 나올 때까지 다시 불러오는 간격·최대 시간 */
const POLL_MS = 3_000;
const POLL_FOR_MS = 3 * 60_000;

/** 이만큼 지나도 요청이 대기 중이면 스케줄러가 멈췄을 수 있다 (스케줄러는 15초마다 요청을 확인) */
const STALE_REQUEST_MS = 60_000;

/** 끄기 확인 창 — 이 작업이 멈추면 생기는 일 */
const STOP_EFFECTS: Record<SchedulerJobKey, string> = {
  EXPIRE_PAYMENTS: '입금 기한 지난 주문이 자동 취소되지 않고, 추가금 요청도 만료되지 않아요.',
  CONFIRM_PURCHASES_AND_SETTLE:
    '구매확정 기한이 지나도 자동 확정되지 않고, 확정된 주문의 정산도 만들어지지 않아요.',
  PURCHASE_CONFIRM_REMINDERS: '구매자에게 자동 구매확정 예정 안내가 가지 않아요.',
  EXPIRE_LICENSES: '계약 기간이 끝난 라이선스가 만료 처리되지 않아 계속 사용 중으로 보여요.',
  LICENSE_EXPIRY_REMINDERS: '만료 7일·3일 전 안내가 구매자·판매자에게 가지 않아요.',
  ROYALTY_REPORT_REMINDERS: '로열티 보고 리마인더와 연체·지연 알림이 가지 않아요.',
  PAYOUT_CLAWBACK_DUE:
    '정산 회수 기한이 지나도 판매자 접속이 정지되지 않고, 기한 임박 알림도 가지 않아요.',
  EXPIRE_PROPOSALS: '응답 기한이 지난 조건 협의가 만료되지 않고 응답 대기로 남아요.',
  EXPIRE_SPONSORSHIP_APPLICATIONS:
    '방문 예정일이 지나도록 확정되지 않은 협찬 지원이 자동 취소되지 않아요.',
  LICENSE_ISSUE_DELAY_REMINDERS: '발급이 늦어진 주문의 판매자에게 발급 지연 안내가 가지 않아요.',
  ANONYMIZE_WITHDRAWN_USERS: `탈퇴 후 ${BUSINESS.WITHDRAWN_USER_RETENTION_DAYS}일이 지난 회원의 개인정보가 지워지지 않고 남아요 — 개인정보 처리방침의 보관 기간을 넘겨요.`,
  CLEANUP_SESSIONS: '오래된 로그인 세션 기록이 지워지지 않고 쌓여요.',
};

const STATUS_CLS: Record<SchedulerJobStatus, string> = {
  RUNNING: 'text-blue-600',
  SUCCESS: 'text-emerald-600',
  FAILED: 'text-rose-600',
};

function durationLabel(ms: number): string {
  if (ms < 1_000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1_000).toFixed(1)}초`;
  return `${Math.floor(ms / 60_000)}분 ${Math.round((ms % 60_000) / 1_000)}초`;
}

const isPending = (job: SchedulerJobResponse) =>
  job.runRequestedAt !== null || job.lastStatus === 'RUNNING';

/**
 * 스케줄러 관리 — 작업별 사용 여부·마지막 실행·지금 실행.
 * 이름·주기는 코드(SCHEDULES)가 정하고, 끄면 정해진 주기에 건너뛴다 ([지금 실행]은 꺼져 있어도 돈다).
 */
export function SchedulerJobsPage() {
  const { session } = useAdminAuth();
  const [rows, setRows] = useState<SchedulerJobResponse[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<SchedulerJobKey | null>(null);
  const [disabling, setDisabling] = useState<SchedulerJobResponse | null>(null);
  /** 실행 요청 뒤 이 시각까지 결과를 기다리며 다시 불러온다 */
  const [pollUntil, setPollUntil] = useState(0);

  const load = useCallback(async () => {
    if (!session) return;
    try {
      setRows(await api.listSchedulerJobs(session.accessToken));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [session]);

  useEffect(() => {
    void load();
  }, [load]);

  // 요청·실행 중인 작업이 남아 있으면 몇 초마다 다시 불러온다 — 결과가 나오거나 최대 시간이 지나면 멈춤
  useEffect(() => {
    if (!rows || !rows.some(isPending) || Date.now() > pollUntil) return;
    const timer = setTimeout(() => void load(), POLL_MS);
    return () => clearTimeout(timer);
  }, [rows, pollUntil, load]);

  if (!session) return null;

  /** 쓰기 공통 — 실패하면 오류를 보여 주고 목록을 다시 불러온다 */
  const write = async (key: SchedulerJobKey, action: () => Promise<unknown>, done: string) => {
    if (busyKey) return;
    setBusyKey(key);
    try {
      await action();
      showToast(done);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyKey(null);
      await load();
    }
  };

  const setEnabled = (job: SchedulerJobResponse, enabled: boolean) =>
    write(
      job.key,
      () => api.updateSchedulerJob(session.accessToken, job.key, { enabled }),
      enabled ? `${job.title} 작업을 켰어요` : `${job.title} 작업을 껐어요`,
    );

  const run = (job: SchedulerJobResponse) =>
    write(
      job.key,
      async () => {
        await api.runSchedulerJob(session.accessToken, job.key);
        setPollUntil(Date.now() + POLL_FOR_MS);
      },
      '실행을 요청했어요. 스케줄러가 15초 안에 시작해요',
    );

  return (
    <main className="mx-auto w-full max-w-6xl px-6 py-8">
      <PageHeader
        title="스케줄러 관리"
        description="정해진 주기로 도는 자동 작업이에요. 끄면 정해진 시각에 건너뛰고, [지금 실행]은 꺼져 있어도 한 번 돌려요. 주기는 코드에서 정해요."
      />
      {error && <ErrorBanner message={error} />}
      {!rows && !error && <LoadingRow />}

      {rows && (
        <TableShell>
          <TheadRow>
            <Th>작업</Th>
            <Th>주기</Th>
            <Th>사용</Th>
            <Th>마지막 실행</Th>
            <Th />
          </TheadRow>
          <tbody>
            {rows.map((job) => (
              <BodyRow key={job.key}>
                <Td>
                  <span className="block font-semibold">{job.title}</span>
                  <span className="block font-mono text-xs text-muted-foreground">{job.key}</span>
                </Td>
                <Td className="whitespace-nowrap text-muted-foreground">{job.scheduleLabel}</Td>
                <Td>
                  <EnabledSwitch
                    checked={job.enabled}
                    label={`${job.title} 사용`}
                    disabled={busyKey !== null}
                    onChange={(next) => (next ? void setEnabled(job, true) : setDisabling(job))}
                  />
                </Td>
                <Td className="max-w-96">
                  <LastRun job={job} />
                </Td>
                <Td className="text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busyKey !== null || job.runRequestedAt !== null}
                    onClick={() => void run(job)}
                  >
                    지금 실행
                  </Button>
                </Td>
              </BodyRow>
            ))}
          </tbody>
        </TableShell>
      )}

      {disabling && (
        <ConfirmDialog
          title={`${disabling.title} 작업을 끌까요?`}
          description={STOP_EFFECTS[disabling.key]}
          confirmLabel="끄기"
          danger
          busy={busyKey !== null}
          onClose={() => setDisabling(null)}
          onConfirm={() => {
            const job = disabling;
            setDisabling(null);
            void setEnabled(job, false);
          }}
        />
      )}
    </main>
  );
}

function LastRun({ job }: { job: SchedulerJobResponse }) {
  const waitingLong =
    job.runRequestedAt !== null &&
    Date.now() - new Date(job.runRequestedAt).getTime() > STALE_REQUEST_MS;
  return (
    <div className="space-y-0.5 text-sm">
      {job.lastStatus === null || job.lastStartedAt === null ? (
        <span className="text-muted-foreground">아직 실행 기록이 없어요</span>
      ) : (
        <p className="tabular-nums">
          <span className={`font-semibold ${STATUS_CLS[job.lastStatus]}`}>
            {SCHEDULER_JOB_STATUS_LABELS[job.lastStatus]}
          </span>
          <span className="ml-2 text-xs text-muted-foreground">
            {formatDateTime(job.lastStartedAt)}
            {job.lastStatus !== 'RUNNING' && job.lastDurationMs !== null
              ? ` · ${durationLabel(job.lastDurationMs)}`
              : ''}
          </span>
        </p>
      )}
      {job.lastStatus === 'FAILED' && job.lastError && (
        <p className="break-words text-xs text-rose-600">{job.lastError}</p>
      )}
      {job.runRequestedAt !== null && (
        <p className={`text-xs ${waitingLong ? 'text-rose-600' : 'text-amber-600'}`}>
          {waitingLong
            ? '실행 요청이 1분 넘게 대기 중이에요 — 스케줄러가 멈췄는지 확인해 주세요'
            : '실행 요청 대기 중'}
        </p>
      )}
    </div>
  );
}

/** 켜짐·꺼짐 스위치 — 켜면 바로, 끄면 확인 창을 거친다 (onChange 에서 정한다) */
function EnabledSwitch({
  checked,
  label,
  disabled,
  onChange,
}: {
  checked: boolean;
  label: string;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex items-center gap-2 disabled:opacity-40"
    >
      <span
        className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition ${checked ? 'bg-brand' : 'bg-border'}`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-[left] ${checked ? 'left-[18px]' : 'left-0.5'}`}
        />
      </span>
      <span
        className={`text-xs font-semibold ${checked ? 'text-foreground' : 'text-muted-foreground'}`}
      >
        {checked ? '켜짐' : '꺼짐'}
      </span>
    </button>
  );
}
